import { Banknote, Building2, Sparkles, PiggyBank, FileText } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useSlideHistory } from '@/features/client-onboarding/stores/onboarding-navigation-store';
import { useOnboardingCompletion } from '@/features/client-onboarding/hooks/use-onboarding-completion';
import { useIsNylPilotAdvisor } from '@/shared/hooks/use-nyl-pilot-advisor';

import {
  zipCodeToState,
  FundingPolicyType,
  calculateAgeFromBirthDate,
  isPolicyEligibleForApplication,
} from '@shared';
import { selectClient, selectFeatureFlags } from '@/model';
import { getStepDefs } from '@/features/client-onboarding/lib/slide-config';

import { INSURANCE_SHOPPING_CONFIG } from '@client';

import { type EmptyStateAddOptionDef } from '@/components/EmptyStateAddButtons';

export type InsuranceState = {
  selectedPolicyType: FundingPolicyType | null;
  selectedPolicyFundingSourceId?: string;
  openInsuranceDetailsModal: boolean;
};

export function useFundingSourcesSlide(clientId: string) {
  const navigate = useNavigate();
  const slideHistory = useSlideHistory();
  const featureFlags = useFeatureFlags();
  const client = useSelector(selectClient)!;
  const { flags } = useFeatureFlags();

  const { clientFirstName, intakeSurvey } = client;

  const advisorFeatureFlags = useSelector(selectFeatureFlags);
  const vbShopFeatureFlag = advisorFeatureFlags.vbShop;
  const agisFeatureFlag = advisorFeatureFlags.agis;
  const isEdjPilot = !!advisorFeatureFlags.edjPilotAdvisor;
  const isGenworthMode = !!advisorFeatureFlags.genworthMode;
  const isNylPilot = useIsNylPilotAdvisor();

  const showGroupPolicy = vbShopFeatureFlag || agisFeatureFlag;

  const [insuranceState, setInsuranceState] = useState<InsuranceState>({
    selectedPolicyType: null,
    selectedPolicyFundingSourceId: undefined,
    openInsuranceDetailsModal: false,
  });
  const [selectedSelfFundingSourceId, setSelectedSelfFundingSourceId] = useState<
    string | undefined
  >(undefined);
  const [openSelfFundingModal, setOpenSelfFundingModal] = useState(false);
  const [openAnnuityDetailsModal, setOpenAnnuityDetailsModal] = useState(false);
  const [openSelectFundingSourceModal, setOpenSelectFundingSourceModal] = useState(false);
  const [openAddInsuranceOptionsModal, setOpenAddInsuranceOptionsModal] = useState(false);
  const [openCcrcTypeAManualSheet, setOpenCcrcTypeAManualSheet] = useState(false);
  const [ccrcTypeAEditFundingSourceId, setCcrcTypeAEditFundingSourceId] = useState<
    string | undefined
  >(undefined);
  const [searchParams, setSearchParams] = useSearchParams();

  const suggestDownloadReportParam = searchParams.get('downloadReport') === 'true';

  useOnboardingCompletion(clientId);

  const handleShowDownloadReport = useCallback(() => {
    if (!suggestDownloadReportParam) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('downloadReport', 'true');
      setSearchParams(newParams, { replace: true });
    }
  }, [suggestDownloadReportParam, searchParams, setSearchParams]);

  const handleOpenShopInsurance = useCallback(() => {
    navigate(`/clients/${clientId}/onboarding/shop-insurance`);
  }, [navigate, clientId]);

  useEffect(() => {
    const shopInsurance = searchParams.get('shopInsurance');

    if (shopInsurance === 'true' && !isEdjPilot) {
      const params = new URLSearchParams();
      const paymentOption = searchParams.get('paymentOption');
      const buyerOption = searchParams.get('buyerOption');
      const selectTabOption = searchParams.get('selectTabOption');

      if (paymentOption) params.set('paymentOption', paymentOption);
      if (buyerOption) params.set('buyerOption', buyerOption);
      if (selectTabOption) params.set('selectTabOption', selectTabOption);

      const query = params.toString();

      navigate(`/clients/${clientId}/onboarding/shop-insurance${query ? `?${query}` : ''}`, {
        replace: true,
      });
    }
  }, [searchParams, navigate, clientId]);

  const isEligibleForShopInsurance = useMemo(() => {
    if (!intakeSurvey.clientBirthDate) return false;

    const age = calculateAgeFromBirthDate(intakeSurvey.clientBirthDate);

    return (
      age >= INSURANCE_SHOPPING_CONFIG.eligibilityAge.min &&
      age <= INSURANCE_SHOPPING_CONFIG.eligibilityAge.max
    );
  }, [intakeSurvey.clientBirthDate]);

  const hasVisitedPersonalizePlan = useMemo(() => {
    const stepDefs = getStepDefs(client);
    const personalizePlanStep = stepDefs.find(step => step.name === 'Personalize Plan');

    if (!personalizePlanStep) return false;

    return slideHistory.some(
      visitedIndex =>
        visitedIndex >= personalizePlanStep.beginningSlideIndex &&
        visitedIndex <= personalizePlanStep.lastSlideIndex,
    );
  }, [client, slideHistory]);

  const openInsuranceDetailsModal = useCallback(
    (
      selectedPolicyType: FundingPolicyType | null = null,
      selectedPolicyFundingSourceId: string | undefined = undefined,
    ) => {
      setInsuranceState({
        selectedPolicyType,
        selectedPolicyFundingSourceId,
        openInsuranceDetailsModal: true,
      });
    },
    [],
  );

  // The freemium intent router routes modal-based intents back here with an
  // ?fxAction param so the matching sheet opens on arrival.
  useEffect(() => {
    const action = searchParams.get('fxAction');
    if (!action) return;

    if (action === 'ccrc') {
      setOpenCcrcTypeAManualSheet(true);
    } else if (action === 'analyze-policy') {
      openInsuranceDetailsModal();
    }

    const next = new URLSearchParams(searchParams);
    next.delete('fxAction');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, openInsuranceDetailsModal]);

  const closeInsuranceDetailsModal = useCallback(() => {
    setInsuranceState({
      selectedPolicyType: null,
      selectedPolicyFundingSourceId: undefined,
      openInsuranceDetailsModal: false,
    });
  }, []);

  const handleClickEditPolicy = (
    selectedPolicyType: FundingPolicyType | null,
    selectedPolicyFundingSourceId: string | undefined,
  ) => {
    const policy = selectedPolicyFundingSourceId
      ? client.multipleFundingSources.ltcPolicy[selectedPolicyFundingSourceId]
      : undefined;

    if (policy?.isLifePlanningCommunityProduct) {
      setCcrcTypeAEditFundingSourceId(selectedPolicyFundingSourceId);
      setOpenCcrcTypeAManualSheet(true);
      return;
    }

    openInsuranceDetailsModal(selectedPolicyType, selectedPolicyFundingSourceId);
  };

  const handleClickEditSelfFunding = (fundingSourceId: string) => {
    setSelectedSelfFundingSourceId(fundingSourceId);
    setOpenSelfFundingModal(true);
  };

  const emptyStateAddOptions: EmptyStateAddOptionDef[] = [
    // Genworth replaces "Add Insurance" (shopping/applying for a new policy) with the
    // rate-action flow: upload a rate-action letter to explore your existing policy options.
    isGenworthMode
      ? {
          title: 'Explore Rate Action',
          testId: 'empty-state-explore-rate-action',
          description: 'Upload your rate action letter to explore your policy options.',
          icon: FileText,
          onClick: () => {
            setOpenSelectFundingSourceModal(false);
            navigate(`/clients/${clientId}/rate-action-letter`);
          },
        }
      : {
          title: 'Add Insurance',
          testId: 'empty-state-add-insurance',
          description: isEdjPilot
            ? 'Client can upload a policy or provide policy details to get started.'
            : 'Upload a policy document or provide policy details to get started.',
          icon: Sparkles,
          onClick: () => {
            // Both pilots withhold Quote & Apply, so skip the options modal and its
            // "Explore Coverage Options" entry and go straight to the Add Policy sheet.
            if (isEdjPilot || isNylPilot) {
              openInsuranceDetailsModal();
              setOpenSelectFundingSourceModal(false);
              return;
            }

            const state = zipCodeToState(client.intakeSurvey.clientZipCode);
            const isStateAllowed =
              INSURANCE_SHOPPING_CONFIG.allowedStates.has(state.stateAbbreviation) ||
              (flags.nycLifeDemo && state.stateAbbreviation === 'NY');

            if (isEligibleForShopInsurance && isStateAllowed) {
              setOpenAddInsuranceOptionsModal(true);
            } else {
              openInsuranceDetailsModal();
            }

            setOpenSelectFundingSourceModal(false);
          },
        },
    {
      title: 'Add a Life Planning Community Product',
      testId: 'empty-state-add-type-a-product',
      description:
        'Model a Life Planning Community, CCRC, or CCAH Type A Plan. These plans typically cover all LTC expenses for a one-time entry and recurring maintenance fee.',
      icon: Building2,
      onClick: () => {
        setOpenSelectFundingSourceModal(false);
        if (advisorFeatureFlags.ccrcTypeAProducts) {
          navigate(`/clients/${clientId}/onboarding/ccrc-type-a`);
          return;
        }
        setCcrcTypeAEditFundingSourceId(undefined);
        setOpenCcrcTypeAManualSheet(true);
      },
    },
    {
      title: 'Allocate Savings/Investments',
      testId: 'empty-state-allocate-savings',
      description: 'Earmark personal funds for long-term care.',
      icon: PiggyBank,
      onClick: () => {
        setSelectedSelfFundingSourceId(undefined);
        setOpenSelfFundingModal(true);
        setOpenSelectFundingSourceModal(false);
      },
    },
    {
      title: 'Add Annuities',
      testId: 'empty-state-add-annuities',
      description: isEdjPilot
        ? 'Client can provide annuity details to track income streams from annuities.'
        : 'Track income streams from annuities.',
      icon: Banknote,
      onClick: () => {
        setOpenAnnuityDetailsModal(true);
        setOpenSelectFundingSourceModal(false);
      },
    },
  ];

  const fundingSourceAdded =
    clientHasPolicyFunding(client) ||
    clientHasAnnuityFunding(client) ||
    clientHasSelfFunding(client);

  const showEmptyStateAddFundingSource = !fundingSourceAdded && !showGroupPolicy;

  const shouldNavigateToUnderwritingDashboard = Object.values(
    client.multipleFundingSources.ltcPolicy,
  )
    .map(ltcPolicy => isPolicyEligibleForApplication(ltcPolicy))
    .some(hasSteps => hasSteps);

  return {
    client,
    clientFirstName,
    featureFlags,
    flags,
    isGenworthMode,
    showGroupPolicy,
    insuranceState,
    setInsuranceState,
    openSelfFundingModal,
    setOpenSelfFundingModal,
    openAnnuityDetailsModal,
    setOpenAnnuityDetailsModal,
    openSelectFundingSourceModal,
    setOpenSelectFundingSourceModal,
    openAddInsuranceOptionsModal,
    setOpenAddInsuranceOptionsModal,
    openCcrcTypeAManualSheet,
    setOpenCcrcTypeAManualSheet,
    ccrcTypeAEditFundingSourceId,
    selectedSelfFundingSourceId,
    suggestDownloadReportParam,
    handleShowDownloadReport,
    handleOpenShopInsurance,
    openInsuranceDetailsModal,
    closeInsuranceDetailsModal,
    handleClickEditPolicy,
    handleClickEditSelfFunding,
    emptyStateAddOptions,
    fundingSourceAdded,
    showEmptyStateAddFundingSource,
    shouldNavigateToUnderwritingDashboard,
    hasVisitedPersonalizePlan,
  };
}

export function clientHasPolicyFunding(client: Client) {
  return Object.entries(client.multipleFundingSources.ltcPolicy).length > 0;
}

export function clientHasAnnuityFunding(client: Client) {
  return client.fundingSources.annuity.hasAnnuity;
}

export function clientHasSelfFunding(client: Client) {
  return Object.values(client.multipleFundingSources.selfFunding).some(selfFunding => {
    const areSelfFundingFieldsPopulated =
      (selfFunding.annualRateOfReturn && selfFunding.annualRateOfReturn > 0) ||
      (selfFunding.monthlyContributionAmount && selfFunding.monthlyContributionAmount > 0) ||
      (selfFunding.existingAssetsValue && selfFunding.existingAssetsValue > 0);

    return selfFunding.hasSelfFunding || areSelfFundingFieldsPopulated;
  });
}
